import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../lib/config';

function Register() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function handleRegister() {
    setError(null);

    if (!email || !password || !confirmPassword) {
      setError('Please fill in all fields.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.detail || 'Registration failed. Please try again.');
        setLoading(false);
        return;
      }

      // Registration successful - go to check email screen
      sessionStorage.setItem('carpeRegisterEmail', email);
      navigate('/check-email');
    } catch {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.logo}>Carpe Wonder™</div>

        <h1 style={styles.headline}>Create your account</h1>
        <p style={styles.subline}>
          Save your traveler profile and never start from scratch again.
        </p>

        <div style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              style={styles.input}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              style={styles.input}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Confirm password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Repeat your password"
              style={styles.input}
            />
          </div>

          {error && <div style={styles.error}>{error}</div>}

          <button
            style={{
              ...styles.button,
              opacity: loading ? 0.6 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
            onClick={handleRegister}
            disabled={loading}
          >
            {loading ? 'Creating account...' : 'Create account →'}
          </button>
        </div>

        <div style={styles.switchRow}>
          Already have an account?{' '}
          <button style={styles.switchBtn} onClick={() => navigate('/login')}>
            Sign in
          </button>
        </div>

        <button style={styles.backBtn} onClick={() => navigate('/')}>
          ← Back to home
        </button>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#0d0d1f',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    padding: '0 16px',
  },
  container: {
    width: '100%',
    maxWidth: '400px',
    paddingTop: '48px',
    paddingBottom: '48px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  logo: {
    fontSize: '14px',
    color: 'var(--gold)',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    marginBottom: '32px',
  },
  headline: {
    fontSize: '26px',
    fontWeight: '500',
    color: '#fff',
    textAlign: 'center',
    marginBottom: '10px',
    lineHeight: '1.2',
  },
  subline: {
    fontSize: '15px',
    color: 'rgba(255,255,255,0.65)',
    textAlign: 'center',
    lineHeight: '1.6',
    marginBottom: '32px',
    maxWidth: '300px',
  },
  form: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginBottom: '24px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.65)',
    letterSpacing: '0.05em',
  },
  input: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '10px',
    padding: '12px 14px',
    fontSize: '15px',
    color: '#fff',
    outline: 'none',
    width: '100%',
    minHeight: '44px',
  },
  error: {
    fontSize: '14px',
    color: '#e07b6b',
    textAlign: 'center',
    padding: '12px',
    background: 'rgba(224,123,107,0.1)',
    borderRadius: '8px',
    lineHeight: '1.5',
  },
  button: {
    width: '100%',
    background: 'var(--gold)',
    border: '2px solid rgba(255,255,255,0.8)',
    borderRadius: '28px',
    padding: '16px',
    fontSize: '15px',
    fontWeight: '600',
    color: '#0d0d1f',
    marginTop: '8px',
    minHeight: '44px',
  },
  switchRow: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.6)',
    marginBottom: '16px',
  },
  switchBtn: {
    background: 'transparent',
    border: 'none',
    color: 'var(--gold)',
    fontSize: '14px',
    cursor: 'pointer',
    padding: '4px',
    minHeight: '44px',
  },
  backBtn: {
    background: 'transparent',
    border: 'none',
    color: 'rgba(255,255,255,0.55)',
    fontSize: '13px',
    cursor: 'pointer',
    padding: '8px 0',
    minHeight: '44px',
  },
};

export default Register;
